import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t, translateError } from '../i18n.js';
import { api } from '../state/auth.js';
import { supportedLocales } from '../i18n.js';
import Button from '../components/shared/Button.jsx';
import Card from '../components/shared/Card.jsx';
import Modal from '../components/shared/Modal.jsx';

const RESOURCES = ['processes', 'tasks', 'templates', 'users', 'documents', 'audit', 'settings', 'reports'];
const ACTIONS = ['create', 'read', 'update', 'delete'];
const CLUB_TYPES = ['algemeen', 'voetbal', 'tennis', 'hockey', 'basketbal', 'judo', 'padel', 'golf', 'zwemmen', 'atletiek', 'volleybal'];

// Why: Map internal resource/action names to nav/locale keys for display
const RESOURCE_LABELS = {
  processes: 'nav.processes', tasks: 'nav.tasks', templates: 'nav.templates',
  users: 'nav.users', documents: 'nav.documents', audit: 'nav.audit',
  settings: 'nav.settings', reports: 'nav.reports'
};
const ACTION_LABELS = {
  create: 'common.create', read: 'platform.seeds.read',
  update: 'common.edit', delete: 'common.delete'
};

export default function SeedDataView() {
  const [language, setLanguage] = useState('nl');
  const [subTab, setSubTab] = useState('roles');
  const locales = supportedLocales.value;

  return (
    <div>
      <div class="flex justify-between items-center mb-4">
        <h2 style="font-size:20px;font-weight:600">{t('platform.seeds.title')}</h2>
        <select class="form-select" style="width:auto;min-width:160px" value={language} onChange={e => setLanguage(e.target.value)}>
          {locales.map(l => (
            <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
          ))}
        </select>
      </div>

      <div class="tabs mb-4">
        <button class={`tab ${subTab === 'roles' ? 'active' : ''}`} onClick={() => setSubTab('roles')}>
          {t('platform.seeds.roles')}
        </button>
        <button class={`tab ${subTab === 'core' ? 'active' : ''}`} onClick={() => setSubTab('core')}>
          {t('platform.seeds.coreTemplates')}
        </button>
        <button class={`tab ${subTab === 'type' ? 'active' : ''}`} onClick={() => setSubTab('type')}>
          {t('platform.seeds.typeTemplates')}
        </button>
      </div>

      {subTab === 'roles' && <SeedRolesPanel language={language} />}
      {subTab === 'core' && <SeedTemplatesPanel language={language} clubType="core" />}
      {subTab === 'type' && <SeedTypeTemplatesPanel language={language} />}
    </div>
  );
}

// ── Roles Panel ──────────────────────────────────────────────────────────────

function SeedRolesPanel({ language }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', permissions: {}, is_admin_role: false, sort_order: 0 });
  const [importing, setImporting] = useState(false);

  const load = () => {
    setLoading(true);
    api(`/api/admin/seeds/roles?language=${language}`).then(setRoles).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [language]);

  const handleSave = async () => {
    if (editing) {
      await api(`/api/admin/seeds/roles/${editing.id}`, { method: 'PUT', body: JSON.stringify({ ...form, language }) });
    } else {
      await api('/api/admin/seeds/roles', { method: 'POST', body: JSON.stringify({ ...form, language }) });
    }
    setShowModal(false);
    setEditing(null);
    load();
  };

  const handleEdit = (role) => {
    setEditing(role);
    setForm({
      name: role.name,
      description: role.description || '',
      permissions: JSON.parse(role.permissions_json || '{}'),
      is_admin_role: !!role.is_admin_role,
      sort_order: role.sort_order || 0
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditing(null);
    setForm({ name: '', description: '', permissions: {}, is_admin_role: false, sort_order: roles.length });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm(t('platform.seeds.confirmDelete'))) return;
    await api(`/api/admin/seeds/roles/${id}`, { method: 'DELETE' });
    load();
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      await api('/api/admin/seeds/import', { method: 'POST' });
      load();
    } catch {} finally {
      setImporting(false);
    }
  };

  const togglePermission = (resource, action) => {
    const perms = { ...form.permissions };
    if (!perms[resource]) perms[resource] = [];
    if (perms[resource].includes(action)) {
      perms[resource] = perms[resource].filter(a => a !== action);
      if (perms[resource].length === 0) delete perms[resource];
    } else {
      perms[resource] = [...perms[resource], action];
    }
    setForm({ ...form, permissions: perms });
  };

  if (loading) return <div class="text-center text-muted" style="padding:40px">{t('common.loading')}</div>;

  return (
    <div>
      <div class="flex justify-between items-center mb-4">
        <Button onClick={handleNew}>{t('platform.seeds.addRole')}</Button>
        {roles.length === 0 && (
          <Button variant="secondary" onClick={handleImport} disabled={importing}>
            {importing ? t('common.loading') : t('platform.seeds.importDefaults')}
          </Button>
        )}
      </div>

      {roles.length === 0 ? (
        <div class="text-center text-muted" style="padding:40px">{t('platform.seeds.noRoles')}</div>
      ) : (
        <div class="card" style="overflow-x:auto">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t('platform.seeds.roleName')}</th>
                <th>{t('platform.seeds.roleDescription')}</th>
                <th>{t('platform.seeds.isAdminRole')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {roles.map(role => (
                <tr key={role.id}>
                  <td>{role.sort_order}</td>
                  <td style="font-weight:500">{role.name}</td>
                  <td class="text-sm text-muted">{role.description}</td>
                  <td>{role.is_admin_role ? <span class="badge badge-actief">✓</span> : '—'}</td>
                  <td>
                    <div class="flex gap-2">
                      <Button variant="secondary" size="sm" onClick={() => handleEdit(role)}>{t('common.edit')}</Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(role.id)}>{t('common.delete')}</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={showModal} title={editing ? t('platform.seeds.editRole') : t('platform.seeds.addRole')} onClose={() => setShowModal(false)}>
        <div class="form-group">
          <label class="form-label">{t('platform.seeds.roleName')}</label>
          <input class="form-input" value={form.name} onInput={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div class="form-group">
          <label class="form-label">{t('platform.seeds.roleDescription')}</label>
          <input class="form-input" value={form.description} onInput={e => setForm({ ...form, description: e.target.value })} />
        </div>
        <div class="form-group">
          <label class="form-label">{t('platform.seeds.sortOrder')}</label>
          <input class="form-input" type="number" value={form.sort_order} onInput={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
        </div>
        <div class="form-group">
          <label class="flex items-center gap-2">
            <input type="checkbox" checked={form.is_admin_role} onChange={e => setForm({ ...form, is_admin_role: e.target.checked })} />
            {t('platform.seeds.isAdminRole')}
          </label>
        </div>

        <div class="form-group">
          <label class="form-label">{t('platform.seeds.permissions')}</label>
          <div style="overflow-x:auto">
            <table class="data-table" style="font-size:12px">
              <thead>
                <tr>
                  <th>{t('platform.seeds.resources')}</th>
                  {ACTIONS.map(a => <th key={a}>{t(ACTION_LABELS[a])}</th>)}
                </tr>
              </thead>
              <tbody>
                {RESOURCES.map(res => (
                  <tr key={res}>
                    <td style="font-weight:500">{t(RESOURCE_LABELS[res])}</td>
                    {ACTIONS.map(action => (
                      <td key={action} style="text-align:center">
                        <input
                          type="checkbox"
                          checked={form.permissions[res]?.includes(action) || false}
                          onChange={() => togglePermission(res, action)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div class="flex gap-2 justify-between mt-4">
          <Button variant="secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave}>{t('common.save')}</Button>
        </div>
      </Modal>
    </div>
  );
}

// ── Templates Panel ──────────────────────────────────────────────────────────

function SeedTemplatesPanel({ language, clubType }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNewTpl, setShowNewTpl] = useState(false);
  const [showNewStep, setShowNewStep] = useState(false);
  const [showNewVariant, setShowNewVariant] = useState(false);
  const [tplForm, setTplForm] = useState({ name: '', description: '', category: '' });
  const [stepForm, setStepForm] = useState({ title: '', description: '', step_order: 1, is_approval_step: false, default_tasks_json: '' });
  const [variantForm, setVariantForm] = useState({ name: '', description: '' });

  const load = () => {
    setLoading(true);
    api(`/api/admin/seeds/templates?language=${language}&clubType=${clubType}`)
      .then(data => { setTemplates(data); if (selected) { const r = data.find(t => t.id === selected.id); setSelected(r || null); } })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { setSelected(null); load(); }, [language, clubType]);

  const createTemplate = async () => {
    const ct = clubType === 'core' ? null : clubType;
    await api('/api/admin/seeds/templates', { method: 'POST', body: JSON.stringify({ ...tplForm, language, club_type: ct }) });
    setShowNewTpl(false);
    setTplForm({ name: '', description: '', category: '' });
    load();
  };

  const deleteTemplate = async (id) => {
    if (!confirm(t('platform.seeds.confirmDelete'))) return;
    await api(`/api/admin/seeds/templates/${id}`, { method: 'DELETE' });
    if (selected?.id === id) setSelected(null);
    load();
  };

  const addStep = async () => {
    await api(`/api/admin/seeds/templates/${selected.id}/steps`, { method: 'POST', body: JSON.stringify(stepForm) });
    setShowNewStep(false);
    setStepForm({ title: '', description: '', step_order: (selected.steps?.length || 0) + 1, is_approval_step: false, default_tasks_json: '' });
    load();
  };

  const deleteStep = async (stepId) => {
    await api(`/api/admin/seeds/steps/${stepId}`, { method: 'DELETE' });
    load();
  };

  const addVariant = async () => {
    await api(`/api/admin/seeds/templates/${selected.id}/variants`, { method: 'POST', body: JSON.stringify(variantForm) });
    setShowNewVariant(false);
    setVariantForm({ name: '', description: '' });
    load();
  };

  const deleteVariant = async (variantId) => {
    await api(`/api/admin/seeds/variants/${variantId}`, { method: 'DELETE' });
    load();
  };

  if (loading) return <div class="text-center text-muted" style="padding:40px">{t('common.loading')}</div>;

  return (
    <div>
      <div class="flex justify-between items-center mb-4">
        <Button onClick={() => setShowNewTpl(true)}>{t('platform.seeds.addTemplate')}</Button>
      </div>

      {templates.length === 0 ? (
        <div class="text-center text-muted" style="padding:40px">{t('platform.seeds.noTemplates')}</div>
      ) : (
        <div class="two-panel">
          <div class="panel-list">
            {templates.map(tpl => (
              <div key={tpl.id} class={`list-item ${selected?.id === tpl.id ? 'list-item-active' : ''}`}
                style="cursor:pointer" onClick={() => setSelected(tpl)}>
                <div style="flex:1">
                  <div style="font-weight:500;font-size:14px">{tpl.name}</div>
                  <div class="text-sm text-muted">{tpl.category}</div>
                </div>
              </div>
            ))}
          </div>

          <div class="panel-detail">
            {selected ? (
              <div>
                <div class="flex justify-between items-center mb-4">
                  <h3 style="font-size:18px;font-weight:600">{selected.name}</h3>
                  <Button variant="danger" size="sm" onClick={() => deleteTemplate(selected.id)}>{t('common.delete')}</Button>
                </div>
                {selected.description && <p class="text-muted mb-4">{selected.description}</p>}

                {/* Steps */}
                <Card title={`${t('template.steps')} (${selected.steps?.length || 0})`} style="margin-bottom:16px"
                  actions={<Button size="sm" variant="secondary" onClick={() => { setStepForm({ ...stepForm, step_order: (selected.steps?.length || 0) + 1 }); setShowNewStep(true); }}>{t('platform.seeds.addStep')}</Button>}>
                  {selected.steps?.map(step => (
                    <div key={step.id} class="list-item">
                      <div style="flex:1">
                        <div style="font-weight:500;font-size:13px">{step.step_order}. {step.title}</div>
                        {step.description && <div class="text-sm text-muted">{step.description}</div>}
                        {step.is_approval_step ? <span class="badge badge-actief" style="font-size:10px">{t('template.isApprovalStep')}</span> : null}
                      </div>
                      <Button variant="danger" size="sm" onClick={() => deleteStep(step.id)}>&times;</Button>
                    </div>
                  ))}
                  {(!selected.steps || selected.steps.length === 0) && <div class="text-sm text-muted">{t('common.noResults')}</div>}
                </Card>

                {/* Variants */}
                <Card title={`${t('template.variants')} (${selected.variants?.length || 0})`}
                  actions={<Button size="sm" variant="secondary" onClick={() => setShowNewVariant(true)}>{t('platform.seeds.addVariant')}</Button>}>
                  {selected.variants?.map(v => (
                    <div key={v.id} class="list-item">
                      <div style="flex:1">
                        <div style="font-weight:500;font-size:13px">{v.name}</div>
                        {v.description && <div class="text-sm text-muted">{v.description}</div>}
                      </div>
                      <Button variant="danger" size="sm" onClick={() => deleteVariant(v.id)}>&times;</Button>
                    </div>
                  ))}
                  {(!selected.variants || selected.variants.length === 0) && <div class="text-sm text-muted">{t('common.noResults')}</div>}
                </Card>
              </div>
            ) : (
              <div class="text-center text-muted" style="padding:60px">{t('platform.seeds.noTemplates')}</div>
            )}
          </div>
        </div>
      )}

      {/* New Template Modal */}
      <Modal isOpen={showNewTpl} title={t('platform.seeds.addTemplate')} onClose={() => setShowNewTpl(false)}>
        <div class="form-group">
          <label class="form-label">{t('platform.seeds.templateName')}</label>
          <input class="form-input" value={tplForm.name} onInput={e => setTplForm({ ...tplForm, name: e.target.value })} />
        </div>
        <div class="form-group">
          <label class="form-label">{t('template.description')}</label>
          <textarea class="form-textarea" value={tplForm.description} onInput={e => setTplForm({ ...tplForm, description: e.target.value })} />
        </div>
        <div class="form-group">
          <label class="form-label">{t('template.category')}</label>
          <input class="form-input" value={tplForm.category} onInput={e => setTplForm({ ...tplForm, category: e.target.value })} />
        </div>
        <div class="flex gap-2 justify-between">
          <Button variant="secondary" onClick={() => setShowNewTpl(false)}>{t('common.cancel')}</Button>
          <Button onClick={createTemplate}>{t('common.create')}</Button>
        </div>
      </Modal>

      {/* New Step Modal */}
      <Modal isOpen={showNewStep} title={t('platform.seeds.addStep')} onClose={() => setShowNewStep(false)}>
        <div class="form-group">
          <label class="form-label">{t('template.stepTitle')}</label>
          <input class="form-input" value={stepForm.title} onInput={e => setStepForm({ ...stepForm, title: e.target.value })} />
        </div>
        <div class="form-group">
          <label class="form-label">{t('template.stepDescription')}</label>
          <textarea class="form-textarea" value={stepForm.description} onInput={e => setStepForm({ ...stepForm, description: e.target.value })} />
        </div>
        <div class="form-group">
          <label class="form-label">{t('template.stepOrder')}</label>
          <input class="form-input" type="number" value={stepForm.step_order} onInput={e => setStepForm({ ...stepForm, step_order: parseInt(e.target.value) || 1 })} />
        </div>
        <div class="form-group">
          <label class="flex items-center gap-2">
            <input type="checkbox" checked={stepForm.is_approval_step} onChange={e => setStepForm({ ...stepForm, is_approval_step: e.target.checked })} />
            {t('template.isApprovalStep')}
          </label>
        </div>
        <div class="flex gap-2 justify-between">
          <Button variant="secondary" onClick={() => setShowNewStep(false)}>{t('common.cancel')}</Button>
          <Button onClick={addStep}>{t('common.create')}</Button>
        </div>
      </Modal>

      {/* New Variant Modal */}
      <Modal isOpen={showNewVariant} title={t('platform.seeds.addVariant')} onClose={() => setShowNewVariant(false)}>
        <div class="form-group">
          <label class="form-label">{t('template.variantName')}</label>
          <input class="form-input" value={variantForm.name} onInput={e => setVariantForm({ ...variantForm, name: e.target.value })} />
        </div>
        <div class="form-group">
          <label class="form-label">{t('template.variantDescription')}</label>
          <textarea class="form-textarea" value={variantForm.description} onInput={e => setVariantForm({ ...variantForm, description: e.target.value })} />
        </div>
        <div class="flex gap-2 justify-between">
          <Button variant="secondary" onClick={() => setShowNewVariant(false)}>{t('common.cancel')}</Button>
          <Button onClick={addVariant}>{t('common.create')}</Button>
        </div>
      </Modal>
    </div>
  );
}

// ── Type Templates Panel (with club type selector) ───────────────────────────

function SeedTypeTemplatesPanel({ language }) {
  const [clubType, setClubType] = useState('voetbal');

  return (
    <div>
      <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px">
        {CLUB_TYPES.map(ct => (
          <Button key={ct} variant={clubType === ct ? 'primary' : 'secondary'} size="sm" onClick={() => setClubType(ct)}>
            {ct.charAt(0).toUpperCase() + ct.slice(1)}
          </Button>
        ))}
      </div>
      <SeedTemplatesPanel language={language} clubType={clubType} />
    </div>
  );
}
